import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { 
	Badge, Card, Collapse, Divider, Empty, Flex, Row, Col,
	Select, Skeleton, Space, Tree, Typography, Statistic 
} from 'antd';
import { 
	ArrowUpOutlined, MinusOutlined, PlusOutlined, 
	DownOutlined, RightOutlined, ClockCircleOutlined,
	NodeIndexOutlined, PlusCircleOutlined, EditOutlined, ShopOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import PropTypes from 'prop-types';

// Actions
import { set_selected_node } from '../../../redux/actions/selfActions';

const { Panel } = Collapse;

const TreeTitle = (props) => {
	const { node, historyId } = props;
	
	const { t } = useTranslation();
	const { file_info } = useSelector(state => state.self);

	const [badgeValues, setbadgeValues] = useState({ count: t('loadStatusDetails.review.noAction.badge'), color: '#8c8c8c' });
	const [loading, setLoading] = useState(false);
	const [routeLocations, setRouteLocations] = useState({ locations: 0, missing: 0, extra: 0 });

	useEffect(() => {
		if (node?.update === 'C') {
			setbadgeValues({ count: 'C', color: '#06c281' });
		}
		else if (node?.update === 'U') {
			setbadgeValues({ count: t('loadStatusDetails.review.update.badge'), color: '#1890ff' });
		}

		if ('route' in node || 'code' in node) {
			const routeFilters = { 
				organization: file_info?.organization?._id?.$oid,
				workapace: file_info?.workspace?._id?.$oid,
				...(file_info?.load && { load: file_info?.load?._id?.$oid }),
				file: file_info?._id?.$oid,
				...(node?.route ? { route: node?.route } : { code: node?.code }),
				history: historyId
			}
			setLoading(true);
			Axios.get(`${process.env.REACT_APP_SERVER_URL}/api/routes/updates/search?${new URLSearchParams(routeFilters).toString()}`)	
				.then((res) => {
					const routeLocations = {
						locations: res.data.locations.length || 0,
						missing: res.data.missing.length || 0,
						extra: res.data.extra.length || 0
					};
					setRouteLocations(routeLocations);
					setLoading(false);
				}).catch((err) => {
					setRouteLocations({});
					setLoading(false);
				});
		}
	}, [node?.update]);

	return (
		<Space>
			<Badge count={badgeValues.count} color={badgeValues.color} />
			<Typography.Text>{node?.title}</Typography.Text>
			{node?.value &&
				<Fragment>
					{loading ? <Skeleton.Input active size="small" /> : (
						<>
							<Typography.Text type='secondary'>/</Typography.Text>
							<Typography.Text style={{ color: '#1677ff' }}><ArrowUpOutlined /> {parseInt(node?.value) - routeLocations.extra}</Typography.Text>
							{routeLocations.extra > 0 && (
								<>
									<Typography.Text type='secondary'>/</Typography.Text>
									<Typography.Text style={{ color: '#389e0d' }}><PlusOutlined /> {routeLocations.extra}</Typography.Text>
								</>
							)}
							{routeLocations.missing > 0 && (
								<>
									<Typography.Text type='secondary'>/</Typography.Text>
									<Typography.Text type='danger'><MinusOutlined /> {routeLocations.missing}</Typography.Text>
								</>
							)}
						</>
					)}
				</Fragment>
			}
		</Space>
	)
};

TreeTitle.propTypes = {
	node: PropTypes.object.isRequired,
	historyId: PropTypes.string
}

const LoadFileReviewCompact = (props) => {
	const { treeData } = props;

	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { isAdminUser } = useSelector(state => state.auth);
	const { selected_node, file_history, file_info } = useSelector(state => state.self);

	const [loadInfo, setLoadInfo] = useState([]);
	const [historyId, setHistoryId] = useState('');

	// Calculate statistics - only count final route elements (those with 'code' and 'route' properties)
	const calculateStats = (data) => {
		let stats = { created: 0, updated: 0, noAction: 0, totalRoutes: 0, totalStores: 0 };
		
		const traverse = (nodes) => {
			if (!Array.isArray(nodes)) return;
			
			nodes.forEach(node => {
				// Only count nodes that have 'code' and 'route' properties (final route elements)
				if (node.hasOwnProperty('code') && node.hasOwnProperty('route')) {
					stats.totalRoutes++;
					stats.totalStores += node.value || 0; // Sum all values (stores)
					
					if (node.update === 'C') stats.created++;
					else if (node.update === 'U') stats.updated++;
					else stats.noAction++;
				}
				
				if (node.children) traverse(node.children);
			});
		};
		
		if (Array.isArray(data)) {
			traverse(data);
		} else if (typeof data === 'object') {
			Object.values(data).forEach(section => traverse([section]));
		}
		
		return stats;
	};

	const stats = calculateStats(treeData);

	const createLoadInfo = (nodes) => {
		if (!Array.isArray(nodes)) return [];

		return nodes.map((node) => {
			const keyNode = `${node.title}-${Math.floor(Math.random() * 1000000)}`;
			let newNode = {
				...node,
				title: <TreeTitle node={node} historyId={historyId} />,
				key: keyNode
			};
	
			if (Array.isArray(node.children)) {
				newNode.children = createLoadInfo(node.children);
			}
	
			return newNode;
		});
	}

	useEffect(() => {
		setHistoryId(file_history.history_latest);
	}, [file_history]);

	useEffect(() => {
		if (historyId !== '') {
			if (Array.isArray(treeData) && treeData.length) {
				setLoadInfo(createLoadInfo(treeData).map(obj => [obj]));
			}
			else {
				if (treeData && typeof treeData === "object" && Object.keys(treeData).length > 0) {
					const newTreeDataArray = [];
					Object.keys(treeData).forEach((key) => {
						newTreeDataArray.push(treeData[key]);
					});
					setLoadInfo(createLoadInfo(newTreeDataArray).map(obj => [obj]));
				}
			}
		}
	}, [treeData, historyId]);

	return (
		<div>
			<Divider />
			
			{/* Collapsible Header with Summary */}
			<Collapse 
				ghost
				expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
				style={{ marginBottom: '16px' }}
			>
				<Panel 
					header={
						<Typography.Title level={4} style={{ margin: 0 }}>
							{t('loadStatusDetails.review.title')}
						</Typography.Title>
					}
					key="1"
				>
					{/* History Selector */}
					{isAdminUser && (
						<Card size="small" style={{ marginBottom: '16px' }}>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Typography.Text strong copyable={{ text: historyId, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
									<ClockCircleOutlined /> {t('loadStatusDetails.validationHistory.title')}
								</Typography.Text>
								<Select
									options={file_history.history_options}
									value={historyId}
									placeholder={t('loadStatusDetails.validationHistory.selectHistoryPlaceholder')}
									style={{ width: '100%' }}
									onChange={(value) => setHistoryId(value)}
									size="default"
								/>
							</Space>
						</Card>
					)}

					{/* Individual Statistics Cards */}
					<Row gutter={16} style={{ marginBottom: '16px' }}>
						<Col span={6}>
							<Card size="small">
								<Statistic
									title="Total de Rutas"
									value={stats.totalRoutes}
									valueStyle={{ fontSize: '16px', color: '#000000' }}
									prefix={<NodeIndexOutlined style={{ color: '#1677ff' }} />}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card size="small">
								<Statistic
									title="Creación de Rutas"
									value={stats.created}
									valueStyle={{ fontSize: '16px', color: '#000000' }}
									prefix={<PlusCircleOutlined style={{ color: '#52c41a' }} />}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card size="small">
								<Statistic
									title="Actualización de Rutas"
									value={stats.updated}
									valueStyle={{ fontSize: '16px', color: '#000000' }}
									prefix={<EditOutlined style={{ color: '#1677ff' }} />}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card size="small">
								<Statistic
									title="Total de Tiendas"
									value={stats.totalStores}
									valueStyle={{ fontSize: '16px', color: '#000000' }}
									prefix={<ShopOutlined style={{ color: '#722ed1' }} />}
								/>
							</Card>
						</Col>
					</Row>

					{/* Legend and Tree Data - Combined Card */}
					<Card size="small" style={{ marginBottom: '16px' }}>
						{/* Legend */}
						<div style={{ marginBottom: '16px' }}>
							<Typography.Text strong style={{ marginBottom: '8px', display: 'block' }}>Leyenda:</Typography.Text>
							<Space wrap>
								<Space size={6}>
									<Badge count={t('loadStatusDetails.review.noAction.badge')} showZero color='#8c8c8c'/>
									<Typography.Text type='secondary'> - {t('loadStatusDetails.review.noAction.desc')}</Typography.Text>
								</Space>
								<Space size={6}>
									<Badge count='C' color='#06c281'/>
									<Typography.Text type='secondary'> - {t('loadStatusDetails.review.creation.desc')}</Typography.Text>
								</Space>
								<Space size={6}>
									<Badge count={t('loadStatusDetails.review.update.badge')} color='#1890ff'/>
									<Typography.Text type='secondary'> - {t('loadStatusDetails.review.update.desc')}</Typography.Text>
								</Space>
							</Space>
						</div>

						{/* Tree Data */}
						<Row justify='center' gutter={16} style={{ borderRadius: '5px', padding: '10px' }}>
							{loadInfo.length > 0
								? loadInfo.map((node, idx) => (
									<Col
										key={idx}
										style={{ borderRight: idx !== loadInfo.length - 1 ? '1px solid #d9d9d9' : 'none' }}
										xl={8} lg={8} md={8} sm={24} xs={24}
									>
										<Tree
											treeData={node}
											onSelect={(selectedKeys, { node }) => {
												if (Number.isFinite(node.value)) {
													dispatch(set_selected_node(node.key === selected_node.key ? {} : node))
												}
											}}
											switcherIcon={({ isLeaf, expanded }) => {
												if (isLeaf) return <span style={{ width: '16px', display: 'inline-block' }} />;
												return expanded ? <DownOutlined /> : <RightOutlined />;
											}}
											showLine={{ showLeafIcon: false }}
											size="small"
										/>
									</Col>
								))
								: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay datos para mostrar" />
							}
						</Row>
					</Card>
				</Panel>
			</Collapse>
		</div>
	);
};

LoadFileReviewCompact.propTypes = {
	treeData: PropTypes.oneOfType([
		PropTypes.array,
		PropTypes.object
	]).isRequired
};

export default LoadFileReviewCompact;